'use client';

import { useEffect, useRef } from 'react';

interface PostBodyProps {
  html: string;
  /** Knowledge base articles number their ordered lists and style Ghost's callout cards. */
  knowledgeBase?: boolean;
}

/*
 * Ghost article HTML carries no classes, so every rule below is a descendant
 * variant on the wrapper. Whole class names only: Tailwind scans for literal
 * strings. Headings map onto type steps, which carry their own responsive
 * branch and set no colour, so text-slate-950 on the wrapper inherits.
 */
const BASE = 'paragraph-xl max-w-none text-slate-950 break-words';
const HEADINGS =
  '[&_h1]:title-2 [&_h2]:title-3 [&_h3]:title-4 [&_h4]:title-5 [&_h5]:title-6 [&_h6]:title-6 ' +
  '[&_h1]:mt-10 [&_h2]:mt-10 [&_h3]:mt-10 [&_h4]:mt-10 [&_h5]:mt-10 [&_h6]:mt-10 ' +
  '[&_h1]:mb-4 [&_h2]:mb-4 [&_h3]:mb-4 [&_h4]:mb-4 [&_h5]:mb-4 [&_h6]:mb-4';
/* TableOfContents marks the heading of the section being read with data-current. */
const CURRENT =
  '[&_h2]:transition-colors [&_h3]:transition-colors [&_h2]:duration-300 [&_h3]:duration-300 [&_[data-current]]:text-scarlet-700';
/* periwinkle-700 is 6.34:1 on white. The 500 step is 3.69:1 and fails for body copy. */
const TEXT =
  '[&_p]:mb-6 [&_strong]:font-semibold [&_a]:link-underline [&_a]:underline-offset-4 [&_a]:text-periwinkle-700';
const LISTS = '[&_ul]:mb-6 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:mb-6 [&_li]:mb-2';
const CODE =
  '[&_code]:rounded-xs [&_code]:border [&_code]:border-slate-200 [&_code]:bg-slate-50 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[0.9em] [&_code]:font-medium ' +
  '[&_pre]:mb-6 [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:border [&_pre]:border-slate-200 [&_pre]:bg-slate-50 [&_pre]:p-6 [&_pre]:font-mono ' +
  '[&_pre_code]:border-0 [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_pre_code]:text-sm';
const BLOCKS =
  '[&_blockquote]:mb-6 [&_blockquote]:border-l-4 [&_blockquote]:border-periwinkle [&_blockquote]:pl-6 [&_blockquote]:italic [&_blockquote]:text-slate-700 ' +
  '[&_img]:my-8 [&_img]:h-auto [&_img]:max-w-full [&_img]:rounded-md ' +
  '[&_hr]:my-12 [&_hr]:border-0 [&_hr]:border-t [&_hr]:border-slate-200';
/* Ghost video and embed cards. Ghost's own player needs theme JavaScript the blog
   does not load, so hide its chrome; transformPostHtml adds the native controls attribute. */
const MEDIA =
  '[&_.kg-video-card]:my-8 [&_.kg-embed-card]:my-8 [&_.kg-video-card_video]:rounded-sm ' +
  '[&_.kg-video-overlay]:hidden [&_.kg-video-player-container]:hidden [&_iframe]:max-w-full';
/* Ghost's bookmark card, which is what a pasted LinkedIn link becomes. LinkedIn sends the whole post as
   the description and a portrait image as the thumbnail, so the text is clamped and the image stays small. */
const BOOKMARK =
  '[&_.kg-bookmark-card]:my-8 [&_.kg-bookmark-container]:flex [&_.kg-bookmark-container]:overflow-hidden ' +
  '[&_.kg-bookmark-container]:rounded-md [&_.kg-bookmark-container]:border [&_.kg-bookmark-container]:border-slate-200 ' +
  '[&_.kg-bookmark-container]:text-inherit [&_.kg-bookmark-container]:no-underline ' +
  '[&_.kg-bookmark-content]:min-w-0 [&_.kg-bookmark-content]:flex-1 [&_.kg-bookmark-content]:p-5 ' +
  '[&_.kg-bookmark-title]:font-semibold [&_.kg-bookmark-title]:text-slate-950 ' +
  '[&_.kg-bookmark-description]:mt-2 [&_.kg-bookmark-description]:line-clamp-3 [&_.kg-bookmark-description]:text-base [&_.kg-bookmark-description]:text-slate-700 ' +
  '[&_.kg-bookmark-metadata]:mt-3 [&_.kg-bookmark-metadata]:flex [&_.kg-bookmark-metadata]:items-center [&_.kg-bookmark-metadata]:gap-2 [&_.kg-bookmark-metadata]:text-sm [&_.kg-bookmark-metadata]:text-slate-600 ' +
  '[&_.kg-bookmark-icon]:size-5 [&_.kg-bookmark-thumbnail]:hidden sm:[&_.kg-bookmark-thumbnail]:block ' +
  '[&_.kg-bookmark-thumbnail]:w-36 [&_.kg-bookmark-thumbnail]:flex-none [&_.kg-bookmark-card_img]:m-0! ' +
  '[&_.kg-bookmark-thumbnail_img]:size-full [&_.kg-bookmark-thumbnail_img]:rounded-none [&_.kg-bookmark-thumbnail_img]:object-cover';

/* Editorial posts keep the browser's numbering. */
const NUMBERED = '[&_ol]:list-decimal [&_ol]:pl-6';
/* Knowledge base: kb-steps replaces the numbering, so the two list utilities
   above stay off this variant rather than fighting it in the cascade. pl-11 is
   the 1.75rem counter badge plus a 1rem gap. Ghost's callout colours land on
   the border only; blue, white, and grey keep periwinkle. */
const KNOWLEDGE_BASE =
  'kb-steps [&_ol]:pl-0 [&_ol>li]:relative [&_ol>li]:my-3 [&_ol>li]:pl-11 ' +
  '[&_.kg-callout-card]:my-6 [&_.kg-callout-card]:flex [&_.kg-callout-card]:gap-3 [&_.kg-callout-card]:rounded-md ' +
  '[&_.kg-callout-card]:border-l-4 [&_.kg-callout-card]:border-periwinkle [&_.kg-callout-card]:bg-slate-50 ' +
  '[&_.kg-callout-card]:px-5 [&_.kg-callout-card]:py-4 [&_.kg-callout-emoji]:flex-none [&_.kg-callout-text]:m-0 ' +
  '[&_.kg-callout-card-green]:border-green [&_.kg-callout-card-yellow]:border-orange [&_.kg-callout-card-accent]:border-orange ' +
  '[&_.kg-callout-card-red]:border-scarlet [&_.kg-callout-card-purple]:border-purple [&_.kg-callout-card-pink]:border-pink';

/**
 * Renders Ghost post HTML. The blog keeps recently visited pages mounted but hidden for instant
 * back navigation, so when this page hides or unmounts the effect cleanup stops any media in it:
 * videos and audio are paused, and iframes are reloaded so an embedded player stops too.
 */
export function PostBody({ html, knowledgeBase = false }: PostBodyProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = ref.current;
    return () => {
      root?.querySelectorAll<HTMLMediaElement>('video, audio').forEach(media => media.pause());
      root?.querySelectorAll('iframe').forEach(frame => frame.setAttribute('src', frame.src));
    };
  }, []);

  const className = [
    BASE,
    HEADINGS,
    CURRENT,
    TEXT,
    LISTS,
    CODE,
    BLOCKS,
    MEDIA,
    BOOKMARK,
    knowledgeBase ? KNOWLEDGE_BASE : NUMBERED,
  ].join(' ');

  return <div ref={ref} className={className} dangerouslySetInnerHTML={{ __html: html }} />;
}
